import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';

const CACHE_KEY_PREFIX = 'color_prefs_';

const useColorPreferences = (authFetch, userId) => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);

  const cacheKey = userId ? `${CACHE_KEY_PREFIX}${userId}` : null;

  // Load from localStorage on mount, then fetch from server
  useEffect(() => {
    if (!userId || !authFetch) return;

    // Load cached first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) setPreferences(JSON.parse(cached));
    } catch { /* ignore */ }

    // Fetch from server
    authFetch(`${API_URL}/api/color-preferences`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.preferences) {
          setPreferences(data.preferences);
          localStorage.setItem(cacheKey, JSON.stringify(data.preferences));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch, userId, cacheKey]);

  // Apply color overrides to a list of users
  const applyToUsers = useCallback((users) => {
    if (!users || Object.keys(preferences).length === 0) return users;
    return users.map(u => {
      const custom = preferences[String(u.id)];
      return custom ? { ...u, color: custom } : u;
    });
  }, [preferences]);

  // Apply color overrides to events (patches assignees and time-off color)
  const applyToEvents = useCallback((events) => {
    if (!events || Object.keys(preferences).length === 0) return events;
    return events.map(event => {
      let patched = event;

      // Time-off events: override event.color by user_id
      if (event.isTimeOff && event.user_id) {
        const custom = preferences[String(event.user_id)];
        if (custom) {
          patched = { ...patched, color: custom };
        }
      }

      // Assignees array: override each assignee's color
      if (event.assignees && event.assignees.length > 0) {
        const newAssignees = event.assignees.map(a => {
          const custom = preferences[String(a.id)];
          return custom ? { ...a, color: custom } : a;
        });
        // Only create new object if something changed
        if (newAssignees.some((a, i) => a !== event.assignees[i])) {
          patched = { ...patched, assignees: newAssignees };
        }
      }

      return patched;
    });
  }, [preferences]);

  // Save preferences (optimistic update + PUT)
  const savePreferences = useCallback(async (newPrefs) => {
    // Strip null entries for local state (nulls are only needed in the
    // request body so the backend can delete those preferences)
    const cleaned = Object.fromEntries(
      Object.entries(newPrefs).filter(([, v]) => v != null)
    );
    setPreferences(cleaned);
    if (cacheKey) {
      localStorage.setItem(cacheKey, JSON.stringify(cleaned));
    }

    try {
      await authFetch(`${API_URL}/api/color-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPrefs }),
      });
    } catch (err) {
      console.error('Error saving color preferences:', err);
    }
  }, [authFetch, cacheKey]);

  return { preferences, loading, applyToUsers, applyToEvents, savePreferences };
};

export default useColorPreferences;

// Clear color preference cache (call on logout)
export const clearColorPreferenceCache = () => {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
  keys.forEach(k => localStorage.removeItem(k));
};
